import React, { useEffect, useState } from 'react';
import { reaction } from 'mobx';
import { observer } from 'src/utils/mobx-react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { useCommon } from 'src/domains/common/Common';
import { UsersState } from 'src/domains/players/state/UsersState';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { RollingNetDepositLimitState } from 'src/domains/players/webview/components/Account/limitsTab/rollingNetDepositLimit/DepositLimitsProcedure.state';
import {
    DepositLimitsEditForm,
    renderLimitApiMessage,
} from 'src/domains/players/webview/components/Account/limitsTab/rollingNetDepositLimit/DepositLimitsProcedure';
import {
    EditLimitsTitle,
    LimitsTypeSection,
} from 'src/domains/players/webview/components/Account/limitsTab/rollingNetDepositLimit/DepositLimitsProcedure.style';
import { getRollingLimits } from 'src_server/trpc/types/responsibleGambling';

const REDIRECT_TO_LIMITS_AFTER_SAVE_DELAY_MS = 1800;

const redirectToLimitsAfterSave = (starRouter: StarRouter): (() => void) => {
    return (): void => {
        setTimeout((): void => {
            starRouter.redirectToLimits();
        }, REDIRECT_TO_LIMITS_AFTER_SAVE_DELAY_MS);
    };
};

const depositLimitsEditTitle = (calculationType: getRollingLimits.CalculationTypeSchemaType): JSX.Element => {
    if (calculationType === 'gross') {
        return (
            <I18n
                langKey='account.deposit-limit-readonly.title'
                defaultText='Deposit Limits'
            />
        );
    }

    return (
        <I18n
            langKey='account.deposit-limit-readonly.net-title'
            defaultText='Net Deposit Limits'
        />
    );
};

const DepositLimitsEditFormForType = observer(
    'DepositLimitsEditFormForType',
    ({
        calculationType,
        showCalculationTypeToggle,
    }: {
        calculationType: getRollingLimits.CalculationTypeSchemaType;
        showCalculationTypeToggle: boolean;
    }): JSX.Element => {
        const common = useCommon();
        const configComponents = ConfigComponents.get(common);
        const usersState = UsersState.get(common);
        const languagesState = LanguagesState.get(common);
        const starRouter = StarRouter.get(common);
        const onSuccessfulDepositLimitSave = redirectToLimitsAfterSave(starRouter);

        const [state] = useState(
            () =>
                new RollingNetDepositLimitState(
                    common,
                    configComponents,
                    usersState,
                    languagesState,
                    onSuccessfulDepositLimitSave,
                    calculationType
                )
        );

        useEffect(() => {
            const disposer = reaction(
                () => state.rollingNetDepositLimitStatus,
                (status): void => {
                    if (status === 'ready') {
                        state.initializeEditFormFromServer();
                    }
                },
                { fireImmediately: true }
            );
            return (): void => {
                disposer();
            };
        }, [state]);

        const onCancel = (): void => {
            starRouter.redirectToLimits();
        };

        return (
            <LimitsTypeSection data-test={`deposit-limits-edit-section-${calculationType}`}>
                {showCalculationTypeToggle ? null : (
                    <EditLimitsTitle data-test={`deposit-limits-edit-title-${calculationType}`}>
                        {depositLimitsEditTitle(calculationType)}
                    </EditLimitsTitle>
                )}
                {state.rollingNetDepositLimitStatus === 'loading' ? (
                    <I18n
                        langKey='account.contact-preferences.loading'
                        defaultText='Loading...'
                    />
                ) : state.rollingNetDepositLimitStatus === 'error' ? (
                    renderLimitApiMessage('error')
                ) : (
                    <DepositLimitsEditForm
                        state={state}
                        onCancel={onCancel}
                        showCalculationTypeToggle={showCalculationTypeToggle}
                    />
                )}
            </LimitsTypeSection>
        );
    }
);

const DepositLimitsEditTabRedesign = observer('DepositLimitsEditTabRedesign', () => {
    const common = useCommon();
    const { depositLimitsStackedView } = ConfigComponents.get(common).config;
    const usersState = UsersState.get(common);
    const selectedCalculationType = usersState.depositLimitsEditCalculationType;

    if (depositLimitsStackedView) {
        return (
            <DepositLimitsEditFormForType
                calculationType={selectedCalculationType}
                showCalculationTypeToggle={false}
            />
        );
    }

    return (
        <DepositLimitsEditFormForType
            calculationType={usersState.depositLimitsEditCalculationType}
            showCalculationTypeToggle={false}
        />
    );
});

export const DepositLimitsEditTab = observer('DepositLimitsEditTab', () => {
    return <DepositLimitsEditTabRedesign />;
});
